import type { ReactNode } from "react";
import { Slider } from "@/OS/components/ui/slider";
import { Switch } from "@/OS/components/ui/switch";
import type { CortexOptions } from "../levels/types";

interface OptionsPanelProps {
  options: CortexOptions;
  onChange: (next: CortexOptions) => void;
  onBack: () => void;
}

function Row({ label, value, children, description }: { label: string; value?: string; description?: string; children: ReactNode }) {

  return (
    <div className="border-b border-white/10 pb-5">
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-sm uppercase tracking-[0.32em] text-white/88">{label}</div>
          {description ? <div className="mt-2 text-sm text-white/55">{description}</div> : null}
        </div>
        {value ? <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#ffb347]">{value}</span> : null}
      </div>
      {children}
    </div>
  );
}

export function OptionsPanel({ options, onChange, onBack }: OptionsPanelProps) {
  return (
    <div className="max-w-3xl space-y-8">
      <section className="space-y-5">
        <div className="font-mono text-[11px] uppercase tracking-[0.38em] text-white/40">Controls</div>
        <Row label="Mouse Sensitivity" value={`${options.mouseSensitivity.toFixed(2)}x`}>
          <Slider value={[options.mouseSensitivity]} min={0.25} max={2} step={0.05} onValueChange={([mouseSensitivity]) => onChange({ ...options, mouseSensitivity })} />
        </Row>
        <Row label="Invert Y" description="Reverse vertical look input.">
          <div className="flex items-center justify-between gap-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/45">{options.invertY ? "Enabled" : "Disabled"}</span>
            <Switch checked={options.invertY} onCheckedChange={(invertY) => onChange({ ...options, invertY })} />
          </div>
        </Row>
      </section>

      <section className="space-y-5">
        <div className="font-mono text-[11px] uppercase tracking-[0.38em] text-white/40">Video</div>
        <Row label="Field of View" value={`${Math.round(options.fov)}°`}>
          <Slider value={[options.fov]} min={60} max={110} step={1} onValueChange={([fov]) => onChange({ ...options, fov })} />
        </Row>
        <Row label="Frame Limiter" value={`${options.frameLimiter} FPS`}>
          <Slider value={[options.frameLimiter]} min={30} max={144} step={6} onValueChange={([frameLimiter]) => onChange({ ...options, frameLimiter })} />
        </Row>
      </section>

      <section className="space-y-5">
        <div className="font-mono text-[11px] uppercase tracking-[0.38em] text-white/40">Audio</div>
        <Row label="Master" value={`${Math.round(options.masterVolume * 100)}%`}>
          <Slider value={[options.masterVolume]} min={0} max={1} step={0.05} onValueChange={([masterVolume]) => onChange({ ...options, masterVolume })} />
        </Row>
        <Row label="SFX" value={`${Math.round(options.sfxVolume * 100)}%`}>
          <Slider value={[options.sfxVolume]} min={0} max={1} step={0.05} onValueChange={([sfxVolume]) => onChange({ ...options, sfxVolume })} />
        </Row>
        <Row label="Music" value={`${Math.round(options.musicVolume * 100)}%`}>
          <Slider value={[options.musicVolume]} min={0} max={1} step={0.05} onValueChange={([musicVolume]) => onChange({ ...options, musicVolume })} />
        </Row>
      </section>

      <div className="border-t border-white/10 pt-5">
        <button type="button" className="font-mono text-[11px] uppercase tracking-[0.32em] text-white/60 transition-colors duration-200 hover:text-[#ffb347] focus-visible:outline-none focus-visible:text-[#ffb347]" onClick={onBack}>
          Back
        </button>
      </div>
    </div>
  );
}
